import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceDashboard } from '../apiUtils';
import {
  CountsData,
  EBData,
  TurfData,
} from '../Reducers/maxdiffAnalysisReducer';

// All MaxDiff analysis thunks live here. New sub-features (choice
// probability, HB, TURF, ...) get their own thunks in this file rather
// than a new one — keeps the import surface small and matches how
// aiAnalysis.ts groups the entire qual analysis surface.

// -------------------------- Filter payload --------------------------
// MaxDiff analysis was originally wired with a pre-resolved
// `filterUserIds` list. That diverged from every other question type,
// where LeftPane.tsx spreads `{...selectedFilters, questionObject}` and
// the backend resolves to user_ids internally (see AnalysisGet.post
// canonical pattern). The `filters` + `questionObject` fields below
// bring MaxDiff into line — same shapes, same backend contract.
// `filterUserIds` is kept as a deprecated fallback for one release; it
// will be removed alongside the backend cleanup PR.

// Demographic filters — same slice used by the standard analysis path
// (state.dashboardReducer.selectedFilters).
export type SelectedFilters = {
  age?: string[];
  gender?: string[];
  state?: string[];
  city?: string[];
  country?: string[];
};

// Cross-question filters — shape matches state.dashboardReducer.selectedComparisonQns.
export type ComparisonQn = {
  questionId: number | string;
  answerType: string;
  answer: any;
};

// Common fields every MaxDiff thunk accepts. Extracted so component
// callers can build one object and hand it to any of the fetch* thunks
// without repeating the shape.
export type MaxDiffFilterArgs = {
  filters?: SelectedFilters | null;
  questionObject?: ComparisonQn[] | null;
  // Deprecated: pre-resolved list. Kept for one release; the backend
  // reads canonical filters first and falls back to this if unset.
  filterUserIds?: number[] | null;
};

// Deterministic cache-key fragment for the filter dimension. Sorted so
// {gender:['M','F']} and {gender:['F','M']} hit the same key; sorted
// questionObject by questionId for the same reason. Kept lightweight —
// used inside Redux reducers so it must run on every dispatch.
export const buildMaxDiffFilterHash = (
  filters?: SelectedFilters | null,
  questionObject?: ComparisonQn[] | null,
): string => {
  const f = filters
    ? JSON.stringify(
        Object.keys(filters)
          .sort()
          .reduce((acc: Record<string, string[]>, k) => {
            const v = (filters as any)[k];
            if (Array.isArray(v) && v.length > 0) {
              acc[k] = [...v].sort();
            }
            return acc;
          }, {}),
      )
    : '';
  const q = questionObject && questionObject.length > 0
    ? JSON.stringify(
        [...questionObject].sort(
          (a, b) => Number(a.questionId) - Number(b.questionId),
        ),
      )
    : '';
  return `${f}|${q}` || 'all';
};

// Assemble the canonical filter part of a MaxDiff request body. Mirrors
// LeftPane.tsx's spread pattern — demographic fields at the top level,
// cross-question filters under `questionObject`. Legacy `filterUserIds`
// forwarded for backwards-compat until the backend cleanup PR.
const buildFilterPayload = (args: MaxDiffFilterArgs) => {
  const { filters, questionObject, filterUserIds } = args;
  return {
    ...(filters || {}),
    questionObject: questionObject && questionObject.length > 0
      ? questionObject
      : undefined,
    // Backend uses ast.literal_eval on filterUserIds, so send a
    // stringified Python-literal-compatible list ("[1, 2, 3]").
    // JSON.stringify produces the same syntax for lists of numbers.
    filterUserIds: filterUserIds
      ? JSON.stringify(filterUserIds)
      : undefined,
  };
};

// -------------------------- Thunks --------------------------

export const fetchMaxDiffCounts = createAsyncThunk<
  CountsData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchCounts', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/counts/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as CountsData;
});

export const fetchMaxDiffEmpiricalBayes = createAsyncThunk<
  EBData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    // Advanced overrides — leave undefined to use backend defaults
    // (k=1.0, iters=500).
    shrinkageK?: number;
    bootstrapIters?: number;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchEmpiricalBayes', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/empirical_bayes/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      shrinkageK: payload.shrinkageK,
      bootstrapIters: payload.bootstrapIters,
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as EBData;
});

export const fetchMaxDiffTurf = createAsyncThunk<
  TurfData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    itemsPool?: number[] | null;   // null / undefined = all items
    comboSize?: number;
    topN?: number;
    appealMode?: 'best' | 'positive_net';
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchTurf', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/turf/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      itemsPool: payload.itemsPool && payload.itemsPool.length > 0
        ? JSON.stringify(payload.itemsPool)
        : undefined,
      comboSize: payload.comboSize,
      topN: payload.topN,
      appealMode: payload.appealMode,
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as TurfData;
});

export type ProbeItem = {
  itemId: number;
  itemName: string;
  // null until a respondent names this item as their best or worst — the
  // derived question is created on demand, so there is nothing to analyse yet.
  questionId: number | null;
  answerType: string;
  // Respondents who explained picking this item as highest / lowest.
  topCount: number;
  bottomCount: number;
};

// 'all' is both sides together (the item tab's All option).
export type ProbeSlot = 'top' | 'bottom' | 'all';

// Cross-question filter entry for a MaxDiff item tab's All / Highest / Lowest
// switch; All sends both sides. The analysis service narrows to respondents whose probe on the
// derived item question explains that pick, so every endpoint the AI pane
// calls (dashboard, themes, verbatims, summary, export) honours it.
export const MAXDIFF_PROBE_SLOT_FILTER = 'maxdiffProbeSlot';

export const probeSlotFilter = (questionId: number, slot: ProbeSlot) => ({
  questionId,
  answerType: MAXDIFF_PROBE_SLOT_FILTER,
  answer: slot === 'all' ? ['top', 'bottom'] : [slot],
});

// Resolves a MaxDiff question to its item roster, each with the derived AVT
// question that holds the probes about that item. Each item is analysed on its
// own ("what do respondents say about iPhone"), so the per-item tabs hand the
// standard AI-analysis pane the questionId returned here and that pane needs
// no MaxDiff awareness.
//
// Not filter-aware on purpose: this is question identity, not data. The pane
// applies the dashboard filters itself, as it does for a real AVT question.
export const fetchMaxDiffProbeQuestions = createAsyncThunk<
  ProbeItem[],
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
  }
>('maxdiffAnalysis/fetchProbeQuestions', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_questions/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
    },
  );
  return (res.data?.data?.items || []) as ProbeItem[];
});

export type ProbeSummary = {
  summaryId: number;
  summary: string;
  createdAt: string;
};

// Derived item question id -> its latest "why highest" / "why lowest"
// summary. A side is missing when it has no summary yet.
export type ProbeSummaries = Record<
  string,
  Partial<Record<ProbeSlot, ProbeSummary>>
>;

// Every item's highest / lowest summary in one call, for the Overview tab.
// Only that tab reads it, so it's fetched there rather than kept in the store.
export const fetchMaxDiffProbeSummaries = async (payload: {
  batchId: number | string;
  questionId: number | string;
}): Promise<ProbeSummaries> => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_summaries/',
    { batchId: payload.batchId, questionId: payload.questionId },
  );
  return (res.data?.data?.summaries || {}) as ProbeSummaries;
};
